// suggestionOps.ts
//
// Derive the suggestion list from the doc (the marks are the source of truth)
// and apply accept/reject. Accept/reject are expressed purely as mark/text
// edits so they sync via Yjs Collaboration like any other change.
import type { Editor } from "@tiptap/react";
import type { EditorState } from "prosemirror-state";
import type { SuggestionType } from "./SuggestionMark";
import { SUGGESTION_INTERNAL_META } from "./suggestionInterceptor";

export interface Suggestion {
  id: string;
  type: SuggestionType;
  authorId: string;
  authorName: string;
  color: string;
  from: number;
  to: number;
  text: string;
}

/** Scan the doc for suggestion marks, grouped by id (a suggestion may span
 *  several text nodes). Sorted by document position. */
export function scanSuggestions(state: EditorState): Suggestion[] {
  const byId = new Map<string, Suggestion>();
  state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    const m = node.marks.find((mk) => mk.type.name === "suggestion");
    if (!m || !m.attrs.id) return;
    const id = m.attrs.id as string;
    const from = pos;
    const to = pos + node.nodeSize;
    const existing = byId.get(id);
    if (existing) {
      existing.from = Math.min(existing.from, from);
      existing.to = Math.max(existing.to, to);
      existing.text += node.text ?? "";
    } else {
      byId.set(id, {
        id,
        type: m.attrs.type as SuggestionType,
        authorId: (m.attrs.authorId as string) ?? "",
        authorName: (m.attrs.authorName as string) ?? "",
        color: (m.attrs.color as string) ?? "#2c1561",
        from,
        to,
        text: node.text ?? "",
      });
    }
  });
  return Array.from(byId.values()).sort((a, b) => a.from - b.from);
}

type Action = "accept" | "reject";

/** Accept/reject one suggestion by id. Re-scans for the current range (positions
 *  shift as other suggestions resolve). keep == text stays (mark removed). */
export function applySuggestion(editor: Editor, id: string, action: Action): void {
  const s = scanSuggestions(editor.state).find((x) => x.id === id);
  if (!s) return;
  const markType = editor.state.schema.marks.suggestion;
  if (!markType) return;
  const keep =
    (s.type === "insert" && action === "accept") ||
    (s.type === "delete" && action === "reject");
  editor
    .chain()
    .command(({ tr }) => {
      // Tag as internal so the suggestion interceptor never re-wraps this
      // resolution into a NEW suggestion. Without it, a "reject insert" /
      // "accept delete" (both tr.delete → ReplaceStep) done by a *different*
      // author while in Suggesting mode cascades into a delete-suggestion.
      tr.setMeta(SUGGESTION_INTERNAL_META, true);
      if (keep) tr.removeMark(s.from, s.to, markType);
      else tr.delete(s.from, s.to);
      return true;
    })
    .run();
}

/** Apply accept/reject to every pending suggestion. Re-scans each pass so
 *  positions stay valid after a resolution shifts the doc. */
export function applyAllSuggestions(editor: Editor, action: Action): void {
  let guard = 0;
  while (guard++ < 2000) {
    const list = scanSuggestions(editor.state);
    if (list.length === 0) break;
    applySuggestion(editor, list[0].id, action);
  }
}
